import OpenAI from 'openai';
import { openAIProxy, isProxyAvailable } from './api/aiProxy';
import { auth, db } from './firebase/config';
import { doc, getDoc } from 'firebase/firestore';

let openaiClient: OpenAI | null = null;

// Helper to check user subscription
async function getUserSubscription(): Promise<{ isPro: boolean; subscription: string }> {
  const user = auth.currentUser;
  if (!user) return { isPro: false, subscription: 'free' };
  
  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const subscription = userDoc.data()?.subscription || 'free';
    const isPro = ['pro', 'premium', 'enterprise'].includes(subscription);
    return { isPro, subscription };
  } catch {
    return { isPro: false, subscription: 'free' };
  }
}

export const initializeOpenAI = (apiKey: string): boolean => {
  try {
    openaiClient = new OpenAI({
      apiKey,
      dangerouslyAllowBrowser: true
    });
    return true;
  } catch (error) {
    console.error('Failed to initialize OpenAI:', error);
    return false;
  }
};

export const enhancePromptSecure = async (
  prompt: string,
  platform: string = 'midjourney',
  apiKey?: string
): Promise<string> => {
  const { isPro } = await getUserSubscription();
  
  // For Pro users, use system API key from environment
  let effectiveApiKey = apiKey;
  if (isPro && !apiKey) {
    effectiveApiKey = process.env.REACT_APP_OPENAI_API_KEY;
  }
  
  if (!effectiveApiKey) {
    throw new Error('API key required. Pro users need system API keys configured.');
  }

  const systemPrompt = `You are a prompt engineering expert. Enhance the following prompt for ${platform}.
  Make it more detailed, artistic, and effective while maintaining the original intent.
  Return only the enhanced prompt without explanations.`;

  try {
    const client = new OpenAI({
      apiKey: effectiveApiKey,
      dangerouslyAllowBrowser: true
    });
    
    const completion = await client.chat.completions.create({
      model: 'gpt-3.5-turbo',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: prompt }
      ],
      temperature: 0.7,
      max_tokens: 500
    });

    return completion.choices[0].message.content || prompt;
  } catch (error) {
    console.error('Error enhancing prompt:', error);
    throw error;
  }
};

export const generateVariationsSecure = async (
  prompt: string,
  count: number = 3,
  apiKey?: string
): Promise<string[]> => {
  const { isPro } = await getUserSubscription();
  
  // For Pro users, use system API key from environment
  let effectiveApiKey = apiKey;
  if (isPro && !apiKey) {
    effectiveApiKey = process.env.REACT_APP_OPENAI_API_KEY;
  }
  
  if (!effectiveApiKey) {
    throw new Error('API key required. Pro users need system API keys configured.');
  }

  const systemPrompt = `Generate ${count} creative variations of the following prompt.
  Each variation should maintain the core concept but explore different artistic styles or perspectives.
  Return as a JSON array with a key "variations" containing the array of strings.`;

  try {
    const client = new OpenAI({
      apiKey: effectiveApiKey,
      dangerouslyAllowBrowser: true
    });
    
    const completion = await client.chat.completions.create({
      model: 'gpt-3.5-turbo',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: prompt }
      ],
      temperature: 0.9,
      max_tokens: 1000
    });

    try {
      const response = JSON.parse(completion.choices[0].message.content || '{"variations": []}');
      return response.variations || [];
    } catch {
      // If not JSON, split by newlines
      const variations = completion.choices[0].message.content
        ?.split('\n')
        .filter(line => line.trim())
        .map(line => line.replace(/^\d+\.\s*/, '')) || [];
      return variations.slice(0, count);
    }
  } catch (error) {
    console.error('Error generating variations:', error);
    throw error;
  }
};

export const convertPromptFormatSecure = async (
  prompt: string,
  fromPlatform: string,
  toPlatform: string,
  apiKey?: string
): Promise<string> => {
  const { isPro } = await getUserSubscription();
  
  // For Pro users, use system API key from environment
  let effectiveApiKey = apiKey;
  if (isPro && !apiKey) {
    effectiveApiKey = process.env.REACT_APP_OPENAI_API_KEY;
  }
  
  if (!effectiveApiKey) {
    throw new Error('API key required. Pro users need system API keys configured.');
  }

  const systemPrompt = `Convert this ${fromPlatform} prompt to work optimally with ${toPlatform}.
  Adjust parameters, style descriptors, and formatting as needed.
  Return only the converted prompt.`;

  try {
    const client = new OpenAI({
      apiKey: effectiveApiKey,
      dangerouslyAllowBrowser: true
    });
    
    const completion = await client.chat.completions.create({
      model: 'gpt-3.5-turbo',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: prompt }
      ],
      temperature: 0.5,
      max_tokens: 500
    });

    return completion.choices[0].message.content || prompt;
  } catch (error) {
    console.error('Error converting prompt:', error);
    throw error;
  }
};

export const suggestImprovementsSecure = async (
  prompt: string,
  apiKey?: string
): Promise<string[]> => {
  const { isPro } = await getUserSubscription();
  
  // For Pro users, use system API key from environment
  let effectiveApiKey = apiKey;
  if (isPro && !apiKey) {
    effectiveApiKey = process.env.REACT_APP_OPENAI_API_KEY;
  }
  
  if (!effectiveApiKey) {
    throw new Error('API key required. Pro users need system API keys configured.');
  }

  const systemPrompt = `Analyze this prompt and suggest 3-5 specific improvements.
  Focus on clarity, artistic detail, and technical parameters.
  Return as a JSON array with a key "suggestions" containing the array of suggestion strings.`;

  try {
    const client = new OpenAI({
      apiKey: effectiveApiKey,
      dangerouslyAllowBrowser: true
    });
    
    const completion = await client.chat.completions.create({
      model: 'gpt-3.5-turbo',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: prompt }
      ],
      temperature: 0.6,
      max_tokens: 500
    });

    try {
      const response = JSON.parse(completion.choices[0].message.content || '{"suggestions": []}');
      return response.suggestions || [];
    } catch {
      // If not JSON, split by newlines
      const suggestions = completion.choices[0].message.content
        ?.split('\n')
        .filter(line => line.trim())
        .map(line => line.replace(/^[-•*]\s*/, '')) || [];
      return suggestions;
    }
  } catch (error) {
    console.error('Error suggesting improvements:', error);
    throw error;
  }
};

// Export all functions
export { isOpenAIInitialized } from './openai';